import type { GridColProps } from "@mantine/core";
import type { GroupedFieldGridColumnSpanOptions } from "@ngt-form/mantine";

import type { GetAppSettingsResponse, SentrySettings } from "./api/Tmd.dtos";
import { icons, type IIconSettings } from "./icons";

export interface IClientSettings {
    baseUrl?: string;
    basePath?: string;
}

interface IDataGridSettings {
    filterDebounceMs: number;
}

interface IFormsGroupedFieldSettings {
    longFieldColumnSpans: GroupedFieldGridColumnSpanOptions;
    shortFieldColumnSpans: GroupedFieldGridColumnSpanOptions;
    sideBySideFieldColumnSpans: GroupedFieldGridColumnSpanOptions;
}

interface IFormsSettings {
    validationDebounceMs: number;
    groupedFields: IFormsGroupedFieldSettings;
    sideBySideColumnSpans: {
        columnOne: GridColProps["span"];
        columnTwo: GridColProps["span"];
    };
}

interface ISentrySettings {
    dsn?: string;
    customer: string;
    environment: string;
    sendDefaultPii: boolean;
    enableMetrics: boolean;
    attachStacktrace: boolean;
    debug: boolean;
    application: string;
    release?: string;
    replaysSessionSampleRate: number;
    replaysOnErrorSampleRate: number;
    enableLogs: boolean;
    tracesSampleRate: number;
    enableReportABug: boolean;
}

interface IAppSettings {
    capabilityColours: {
        required: string;
        optional: string;
        unspecified: string;
    };
    client: IClientSettings;
    dataGrid: IDataGridSettings;
    debug: boolean;
    environment: "Development" | "Test" | "Production";
    forms: IFormsSettings;
    icons: IIconSettings;
    sentry: ISentrySettings;
}

declare global {
    var APP_SETTINGS: GetAppSettingsResponse | undefined;
}

const ClientAppSettingsResponse: GetAppSettingsResponse | undefined = globalThis.APP_SETTINGS;

/**
 * Reads a nullish-filtered Sentry property from the captured server settings object.
 *
 * Postconditions: Missing property names and nullish values return undefined; false, zero and
 * empty strings are retained. The generic cast performs no runtime conversion.
 */
const getSentryAppSettingValue = <TData>(
    appSettingProperty: keyof SentrySettings | undefined
): TData | undefined => {
    if (!appSettingProperty) {
        return undefined;
    }

    const value: TData | undefined = ClientAppSettingsResponse?.appSettings?.sentrySettings?.[
        appSettingProperty
    ] as TData;
    if (value === undefined || value === null) {
        return undefined;
    }

    return value;
};

/**
 * Reads a named build-environment value, treating missing, null and empty values as absent.
 *
 * Assumptions: Whitespace is retained and values are consumed using the existing string contract.
 */
const getSentryEnvironmentValue = (environmentProperty: string | undefined): string | undefined => {
    if (!environmentProperty) {
        return undefined;
    }

    const value: string | undefined = import.meta.env[environmentProperty];

    if (value === undefined || value === null || value === "") {
        return undefined;
    }

    return value;
};

const getSentryEnvironmentString = (
    environmentProperty: string | undefined
): string | undefined => {
    return getSentryEnvironmentValue(environmentProperty);
};

/**
 * Parses a nonempty environment value with parseFloat.
 *
 * Postconditions: Missing values or thrown errors return undefined. Invalid numeric text can
 * return NaN, and numeric prefixes are accepted; neither is normalized or range-checked.
 */
const getSentryEnvironmentNumber = (
    environmentProperty: string | undefined
): number | undefined => {
    const valueStr = getSentryEnvironmentValue(environmentProperty);

    if (!valueStr) {
        return undefined;
    }

    try {
        return parseFloat(valueStr);
    } catch {
        return undefined;
    }
};

/**
 * Recognizes case-insensitive true/false and 1/0 environment strings.
 *
 * Postconditions: Other values return undefined; surrounding whitespace is not trimmed.
 */
const getSentryEnvironmentBoolean = (
    environmentProperty: string | undefined
): boolean | undefined => {
    const valueStr = getSentryEnvironmentValue(environmentProperty);

    if (!valueStr) {
        return undefined;
    }

    const lowerCase = valueStr.toLowerCase();

    if (lowerCase === "1" || lowerCase === "true") {
        return true;
    }
    if (lowerCase === "0" || lowerCase === "false") {
        return false;
    }

    return undefined;
};

/**
 * Selects a truthy server string, then a truthy environment string.
 *
 * Postconditions: Returns undefined when neither is truthy; empty server strings permit fallback.
 */
const getSentryString = (
    appSettingProperty: keyof SentrySettings | undefined,
    environmentProperty: string | undefined
) => {
    const appSettingValue = getSentryAppSettingValue<string>(appSettingProperty);

    if (appSettingValue) {
        return appSettingValue;
    }

    const environmentValue = getSentryEnvironmentString(environmentProperty);

    if (environmentValue) {
        return environmentValue;
    }

    return undefined;
};

/**
 * Selects a non-nullish server number, then a non-nullish parsed environment number.
 *
 * Postconditions: Zero and NaN are retained; neither source is range-checked.
 */
const getSentryNumber = (
    appSettingProperty: keyof SentrySettings | undefined,
    environmentProperty: string | undefined
) => {
    const appSettingValue = getSentryAppSettingValue<number>(appSettingProperty);

    if (appSettingValue !== null && appSettingValue !== undefined) {
        return appSettingValue;
    }

    const environmentValue = getSentryEnvironmentNumber(environmentProperty);

    if (environmentValue !== null && environmentValue !== undefined) {
        return environmentValue;
    }

    return undefined;
};

/**
 * Selects a non-nullish server boolean, then a recognized environment boolean.
 *
 * Postconditions: Explicit false is retained; absent values leave default selection to the caller.
 */
const getSentryBoolean = (
    appSettingProperty: keyof SentrySettings | undefined,
    environmentProperty: string | undefined
) => {
    const appSettingValue = getSentryAppSettingValue<boolean>(appSettingProperty);

    if (appSettingValue !== null && appSettingValue !== undefined) {
        return appSettingValue;
    }

    const environmentValue = getSentryEnvironmentBoolean(environmentProperty);

    if (environmentValue !== null && environmentValue !== undefined) {
        return environmentValue;
    }

    return undefined;
};

/**
 * Captures client layout, service and telemetry settings at module initialization.
 *
 * Assumptions: APP_SETTINGS is supplied before this module loads. Derived settings are not
 * reactively refreshed when that global changes. Numeric and boolean defaults use nullish fallback.
 */
const AppSettings: IAppSettings = {
    capabilityColours: {
        required: "error",
        optional: "warning",
        unspecified: "gray"
    },
    client: {
        baseUrl: import.meta.env.VITE_API_URL,
        basePath: undefined
    },
    dataGrid: {
        filterDebounceMs: 250
    },
    debug: import.meta.env.DEV,
    environment: "Development",
    forms: {
        validationDebounceMs: 500,
        groupedFields: {
            longFieldColumnSpans: {
                label: {
                    base: 12,
                    md: 6,
                    lg: 3,
                    xl: 3
                },
                field: {
                    base: 12,
                    md: 6,
                    lg: 9,
                    xl: 9
                }
            },
            shortFieldColumnSpans: {
                label: {
                    base: 12,
                    md: 6,
                    lg: 3,
                    xl: 3
                },
                field: {
                    base: 12,
                    md: 6,
                    lg: 3,
                    xl: 3
                }
            },
            sideBySideFieldColumnSpans: {
                label: {
                    base: 12,
                    md: 6,
                    lg: 6,
                    xl: 6
                },
                field: {
                    base: 12,
                    md: 6,
                    lg: 6,
                    xl: 6
                }
            }
        },
        sideBySideColumnSpans: {
            columnOne: {
                base: 12,
                md: 12,
                lg: 6,
                xl: 6
            },
            columnTwo: {
                base: 12,
                md: 12,
                lg: 6,
                xl: 6
            }
        }
    },
    icons,
    sentry: {
        dsn: getSentryString("dsn", "VITE_SENTRY_DSN"),
        debug: getSentryBoolean("debug", "VITE_SENTRY_DEBUG") ?? false,
        enableMetrics: getSentryBoolean("enableMetrics", "VITE_SENTRY_ENABLE_METRICS") ?? false,
        sendDefaultPii: getSentryBoolean("sendDefaultPii", "VITE_SENTRY_SEND_DEFAULT_PII") ?? false,
        attachStacktrace:
            getSentryBoolean("attachStacktrace", "VITE_SENTRY_ATTACH_STACKTRACE") ?? false,
        customer: getSentryString("customer", "VITE_SENTRY_CUSTOMER") ?? "Unknown",
        environment: getSentryString("environment", "VITE_SENTRY_ENVIRONMENT") ?? "Development",
        application:
            getSentryString(undefined, "VITE_SENTRY_APPLICATION") ?? "SourceDataVerification",
        release: getSentryString(undefined, "VITE_SENTRY_RELEASE"),
        enableLogs: getSentryBoolean("enableLogs", "VITE_SENTRY_ENABLE_LOGS") ?? false,
        replaysOnErrorSampleRate:
            getSentryNumber(
                "replaysOnErrorSampleRate",
                "VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE"
            ) ?? 1.0,
        replaysSessionSampleRate:
            getSentryNumber("replaysSessionSampleRate", "VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE") ??
            0.1,
        tracesSampleRate:
            getSentryNumber("tracesSampleRate", "VITE_SENTRY_TRACE_SAMPLE_RATE") ?? 0.2,
        enableReportABug:
            getSentryBoolean("enableReportABug", "VITE_SENTRY_ENABLE_REPORT_A_BUG") ?? false
    }
};

export default AppSettings;
